/**
 * The catalogue's common currency — the port of `LibraryFoundation.Entry`.
 *
 * `LibrarySound` (in `model.ts`) describes a drum or a bass as a *recipe*: layers of patches
 * that `renderer.ts` mixes. That shape cannot describe everything the library ships. A
 * reverse swell is a decay played backwards; a vinyl bed is a Poisson process; a drum loop is
 * a dozen one-shots mixed onto a grid with their tails wrapped back onto the head. Those are
 * assembled, not layered.
 *
 * An `LibraryEntry` is what both collapse to: a name, the metadata an asset record needs, and
 * a closure that returns finished audio. One catalogue, one install path, one progress count.
 *
 * This file also owns the two things the assembled sounds need and the layered ones do not:
 * the *loudness* finishing stage (active RMS rather than the K-weighted family policy, which
 * only makes sense for a kit), and the vocal-tract filter bank.
 */

import type { PadColor, SampleLicence } from '../../model/types';
import {
  Biquad,
  applyGain,
  dbToLinear,
  frameCount,
  linearToDB,
  peakOf,
  type RenderBudget,
  type RenderedAudio,
} from '../offline-render';
import { SeededRandom } from '../offline-render';
import { librarySeed } from './identity';
import { mixInto, renderLibrarySound } from './renderer';
import { noteName } from './notes';
import {
  libraryCategoryChokeGroup,
  libraryCategoryPadColor,
  libraryCategorySymbolName,
  librarySoundFileName,
  librarySoundTags,
  type LibrarySound,
} from './model';

// MARK: - Licence

/**
 * Stamped on every sound the library generates.
 *
 * The wording is the same string the iPhone build writes, and it carries the provenance
 * rather than just the licence name: the marketplace's publish guard reads
 * `allowsRedistribution` to decide whether a pack may be sold, and a reviewer reading the
 * manifest needs to see *why* a hundred samples are all CC0 without taking it on trust.
 */
export function libraryContentLicence(): SampleLicence {
  return {
    name: 'Generated by PadLab - CC0',
    url: 'https://creativecommons.org/publicdomain/zero/1.0/',
    allowsRedistribution: true,
  };
}

// MARK: - Notes

const FILE_NAMES = ['C', 'Cs', 'D', 'Ds', 'E', 'F', 'Fs', 'G', 'Gs', 'A', 'As', 'B'] as const;
const PITCH_CLASSES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const;

/**
 * "Cs3" — the note as a file-name token. A `#` in a file name is a fragment marker in a URL
 * and gets mangled by half the tools that touch it, which is why the sharp is spelled out.
 */
export function noteFileToken(midi: number): string {
  return `${FILE_NAMES[((midi % 12) + 12) % 12]!}${Math.floor(midi / 12) - 1}`;
}

/** "C#" — the pitch class alone, for a chord symbol or a `key:` tag. */
export function notePitchClass(midi: number): string {
  return PITCH_CLASSES[((midi % 12) + 12) % 12]!;
}

/**
 * `count` semitones starting at `midi`. Thirteen gives a full octave with both ends, which is
 * what fits a 4 × 4 pad grid with room for a transpose control.
 */
export function chromatic(from: number, count: number): number[] {
  const notes: number[] = [];
  for (let index = 0; index < Math.max(count, 1); index++) notes.push(from + index);
  return notes;
}

// MARK: - Entries

/** Coarse bucket for the pack browser and the library's collection chips. */
export type LibraryFamily = 'drums' | 'bass' | 'melodic' | 'chords' | 'fx' | 'loops';

export interface LibraryRenderContext {
  sampleRate: number;
  budget?: RenderBudget;
}

export interface LibraryEntry {
  /** Stable slug, unique across the whole library. Also the seed source and the pack key. */
  id: string;
  /**
   * Portable file name, e.g. `melodic-bell-Cs5.wav`. Stored as the asset's
   * `originalFileName`, which is what makes generation resumable: a sound already on disk is
   * already in that set, so a reload continues rather than starting again.
   */
  fileName: string;
  name: string;
  caption: string;
  /** Tags as they land on the asset, including the machine-readable root and tempo. */
  tags: string[];
  family: LibraryFamily;
  /** Sub-bucket — the instrument, kit piece or FX family. */
  group: string;
  color: PadColor;
  symbolName: string;
  /** Set for pitched material, so a row of pads can be played as an instrument. */
  rootMidi?: number;
  /** Set for loops. Written straight onto `SampleAsset.detectedBPM`. */
  detectedBPM?: number;
  /** True when the sound is built to be held down and repeated. */
  loops: boolean;
  chokeGroup?: number;
  render: (context: LibraryRenderContext) => Promise<RenderedAudio>;
}

/**
 * Tags as they land on the asset: whatever the sound declared, plus the machine-readable
 * root and tempo so search and tempo matching work without parsing names.
 */
function resolveTags(tags: readonly string[], rootMidi?: number, detectedBPM?: number): string[] {
  const all: string[] = [];
  for (const tag of tags) if (!all.includes(tag)) all.push(tag);
  if (rootMidi !== undefined) {
    all.push(`root:${noteName(rootMidi)}`);
    all.push('pitched');
  }
  if (detectedBPM !== undefined) all.push(`${Math.round(detectedBPM)} bpm`);
  return all;
}

export interface MakeLibraryEntryOptions {
  id: string;
  fileName: string;
  name: string;
  caption: string;
  tags: string[];
  family: LibraryFamily;
  group: string;
  color: PadColor;
  symbolName: string;
  rootMidi?: number;
  detectedBPM?: number;
  loops?: boolean;
  chokeGroup?: number;
  render: (context: LibraryRenderContext) => Promise<RenderedAudio>;
}

export function makeLibraryEntry(options: MakeLibraryEntryOptions): LibraryEntry {
  return {
    id: options.id,
    fileName: options.fileName,
    name: options.name,
    caption: options.caption,
    tags: resolveTags(options.tags, options.rootMidi, options.detectedBPM),
    family: options.family,
    group: options.group,
    color: options.color,
    symbolName: options.symbolName,
    ...(options.rootMidi === undefined ? {} : { rootMidi: options.rootMidi }),
    ...(options.detectedBPM === undefined ? {} : { detectedBPM: options.detectedBPM }),
    loops: options.loops ?? false,
    ...(options.chokeGroup === undefined ? {} : { chokeGroup: options.chokeGroup }),
    render: options.render,
  };
}

/**
 * Wraps a layered drum or bass recipe as a catalogue entry.
 *
 * The 808s and the bass patches carry a root note; the drums do not. That is the difference
 * between a pad grid you can play a line on and a grid of noises, and it is the reason the
 * chromatic 808 set exists at all.
 */
export function soundEntry(sound: LibrarySound, options: { rootMidi?: number } = {}): LibraryEntry {
  const choke = libraryCategoryChokeGroup(sound.category);
  const family: LibraryFamily = sound.category === '808' || sound.category === 'bass' ? 'bass' : 'drums';
  return makeLibraryEntry({
    id: sound.id,
    fileName: librarySoundFileName(sound),
    name: sound.name,
    caption: sound.caption,
    tags: librarySoundTags(sound),
    family,
    group: sound.category,
    color: libraryCategoryPadColor(sound.category),
    symbolName: libraryCategorySymbolName(sound.category),
    ...(options.rootMidi === undefined ? {} : { rootMidi: options.rootMidi }),
    ...(choke === null ? {} : { chokeGroup: choke }),
    render: (context) =>
      renderLibrarySound(sound, {
        sampleRate: context.sampleRate,
        ...(context.budget === undefined ? {} : { budget: context.budget }),
      }),
  });
}

// MARK: - Randomness

/**
 * The seeded stream an assembled sound draws from — pop positions in a vinyl bed, velocity
 * variation in a loop.
 *
 * `SeededRandom` wants the 64-bit state as two halves, and `librarySeed` produces it as a
 * `bigint`, so this is the one place the two representations meet. It is a named function
 * rather than an inline pair of shifts because getting it wrong is silent: the audio still
 * renders, it just stops being the same audio next time.
 */
export function libraryRandom(slug: string, slot = 0): SeededRandom {
  const seed = librarySeed(slug, slot);
  return new SeededRandom(Number((seed >> 32n) & 0xffffffffn), Number(seed & 0xffffffffn));
}

/** A 0…9999 seed for the glitch effect node, derived from the same stable hash. */
export function librarySeedNumber(slug: string, slot = 0): number {
  return Number(librarySeed(slug, slot) % 9973n);
}

// MARK: - Levelling

/** Peak every finished sound is held under, matching `LIBRARY_CEILING_DB` in the renderer. */
const CEILING_DB = -1;

/**
 * RMS over the part of the sound that is actually sounding.
 *
 * A plain RMS over the whole buffer would report a two-second reverb tail as "quiet" and a
 * half-second stab as "loud" purely because of how much silence each carries. Measuring only
 * frames within 40 dB of the peak is what makes a pad and a pluck comparable, which is the
 * entire point of levelling the pack.
 */
export function activeRMSDB(channels: readonly Float32Array[]): number {
  const frames = frameCount(channels);
  if (frames === 0) return -180;

  const threshold = peakOf(channels) * dbToLinear(-40);
  if (threshold <= 0) return -180;

  let sum = 0;
  let counted = 0;
  for (let frame = 0; frame < frames; frame++) {
    let loudest = 0;
    for (const channel of channels) loudest = Math.max(loudest, Math.abs(channel[frame]!));
    if (loudest < threshold) continue;
    counted++;
    for (const channel of channels) {
      const value = channel[frame]!;
      sum += value * value;
    }
  }
  if (counted === 0) return -180;
  return linearToDB(Math.sqrt(sum / (counted * channels.length)));
}

/**
 * The one place an assembled library sound gets its final level.
 *
 * `renderPatch` peak-normalises everything it renders, which lines up the *peaks* of a soft
 * pad and a hard stab and therefore lines up nothing anyone hears. This scales to a loudness
 * target instead, then pulls the whole thing back only if that pushed a peak over the ceiling
 * — so a sound with an unusually spiky crest factor comes out a little quieter rather than
 * clipped.
 */
export function finishToRMS(channels: Float32Array[], targetRMSDB: number): void {
  if (channels.length === 0) return;
  const measured = activeRMSDB(channels);
  if (measured <= -120) return;

  // Bounded so a pathological measurement can never turn a sound into noise or into silence;
  // 18 dB is far more correction than any patch here needs.
  applyGain(channels, Math.min(Math.max(targetRMSDB - measured, -18), 18));

  const peakAfter = linearToDB(peakOf(channels));
  if (peakAfter > CEILING_DB) applyGain(channels, CEILING_DB - peakAfter);
}

/**
 * Raised-cosine fade at the head and tail.
 *
 * Used by anything assembled outside `renderPatch` — reversed buffers, tape stops, crackle
 * beds — which would otherwise start or end on a step.
 */
export function applyEdgeFades(
  channels: Float32Array[],
  headSeconds: number,
  tailSeconds: number,
  sampleRate: number,
): void {
  const frames = frameCount(channels);
  if (frames <= 2 || sampleRate <= 0) return;

  const head = Math.min(Math.trunc(headSeconds * sampleRate), Math.trunc(frames / 2));
  const tail = Math.min(Math.trunc(tailSeconds * sampleRate), Math.trunc(frames / 2));

  for (const channel of channels) {
    if (head > 1) {
      for (let index = 0; index < head; index++) {
        channel[index]! *= 0.5 - 0.5 * Math.cos(Math.PI * (index / (head - 1)));
      }
    }
    if (tail > 1) {
      const start = frames - tail;
      for (let offset = 0; offset < tail; offset++) {
        channel[start + offset]! *= 0.5 + 0.5 * Math.cos(Math.PI * (offset / (tail - 1)));
      }
    }
  }
}

/**
 * Mixes `source` into a fixed-length loop buffer, wrapping anything that overhangs the end
 * back onto the head.
 *
 * This is what makes a generated loop actually loop: an open hat on the last sixteenth rings
 * into the top of the bar the way it would if the pattern were playing continuously, instead
 * of being chopped off at the loop point. Two calls to `mixInto` do it — the second at a
 * negative frame, which the mixer already reads as "skip this much of the source".
 */
export function mixWrapped(
  destination: Float32Array[],
  source: readonly Float32Array[],
  atFrame: number,
  gain: number,
  pan: number,
): void {
  const length = frameCount(destination);
  if (length === 0 || source.length === 0) return;

  // One wrap only. A hit longer than the loop it lives in would smear over itself forever, so
  // it is trimmed to the loop length first.
  const clipped = frameCount(source) > length ? source.map((channel) => channel.subarray(0, length)) : source;

  const start = ((atFrame % length) + length) % length;
  mixInto(destination, clipped, start, gain, pan);
  mixInto(destination, clipped, start - length, gain, pan);
}

// MARK: - Vocal tract

/**
 * Vowel shapes for PadLab's *synthetic* vocal sounds.
 *
 * These are not recordings and are never described as vocals in the app: they are an
 * oscillator through a bank of resonators, which is the same trick a formant synth has used
 * since the 1970s. Calling them "vocal samples" would be a lie a buyer could hear.
 */
export type Vowel = 'ah' | 'oh' | 'ee';

interface Formant {
  frequency: number;
  q: number;
  gain: number;
}

/**
 * Measured formant centres for a relaxed adult voice. The Q values are the part that matters
 * most: too low and it is a wah pedal, too high and it rings like a bell instead of a throat.
 */
function formantsFor(vowel: Vowel): Formant[] {
  switch (vowel) {
    case 'ah':
      return [
        { frequency: 730, q: 9, gain: 1.0 },
        { frequency: 1090, q: 11, gain: 0.55 },
        { frequency: 2440, q: 13, gain: 0.22 },
      ];
    case 'oh':
      return [
        { frequency: 450, q: 8, gain: 1.0 },
        { frequency: 800, q: 10, gain: 0.5 },
        { frequency: 2830, q: 14, gain: 0.16 },
      ];
    case 'ee':
      return [
        { frequency: 270, q: 8, gain: 1.0 },
        { frequency: 2290, q: 12, gain: 0.5 },
        { frequency: 3010, q: 14, gain: 0.25 },
      ];
  }
}

/**
 * Parallel band-pass resonators, summed, plus a little of the dry source.
 *
 * The dry path is not decoration. Three narrow band-passes at 730/1090/2440 Hz throw away the
 * fundamental of anything sung below about 400 Hz, and a note with no fundamental is a note a
 * tuner — and a listener stacking it under a bass — will disagree about. Fifteen percent dry
 * keeps the whole harmonic series present at −16 dB while the resonances still do all the
 * character.
 */
export function applyFormantBank(
  channels: Float32Array[],
  vowel: Vowel,
  sampleRate: number,
  dryMix = 0.15,
): void {
  if (channels.length === 0 || sampleRate <= 0) return;
  const formants = formantsFor(vowel);
  const dry = Math.min(Math.max(dryMix, 0), 1);

  for (const channel of channels) {
    const filters = formants.map((formant) => new Biquad().set('bandPass', formant.frequency, formant.q, sampleRate));
    for (let index = 0; index < channel.length; index++) {
      const input = channel[index]!;
      let wet = 0;
      for (let stage = 0; stage < filters.length; stage++) wet += filters[stage]!.process(input) * formants[stage]!.gain;
      channel[index] = wet + input * dry;
    }
  }
}
